// ...existing code...
import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef, useState } from 'react';
import { darkTheme, lightTheme } from '../../../components/highcharts/themes';
import { formatNumber } from '@/utils/formatters';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

const PortfolioAnalysisGroupedColumnChart = ({
  data,
  isLoading,
  unit = 'kWh',
}) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();

  const [mode, setMode] = useState('month');
  // Extract categories (months) from the data
  const months =
    data?.this_year?.map((item) => {
      const [year, month] = item.year_month.split('-'); // Extract year and month
      const date = new Date(year, month - 1); // Convert to Date object
      return date.toLocaleString('da-DK', { month: 'short', year: '2-digit' }); // Get short month name (e.g., "Jan")
    }) || [];

  // Extract consumption values
  const thisYearData = data?.this_year?.map((item) => item.consumption) || [];
  const lastYearData = data?.last_year?.map((item) => item.consumption) || [];
  const previousYearData =
    data?.previous_year?.map((item) => item.consumption) || [];

  // Helper to sum arrays (handles numbers or numeric strings)
  const sum = (arr) =>
    (arr || []).reduce((acc, v) => acc + (Number(v) || 0), 0);

  // Aggregated summary data (three bars)
  const summaryCategories = [
    '24-36 måneder',
    '12-24 måneder',
    'Seneste 12 måneder',
  ];
  const summaryData = [
    sum(previousYearData),
    sum(lastYearData),
    sum(thisYearData),
  ];

  // Colors for the three summary bars (use theme where possible)

  // summary series are built inline for the yearly (summary) chart

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);

  // Build options depending on mode
  const options =
    mode === 'month'
      ? {
          chart: {
            type: 'column',
          },
          title: {
            text: ``,
          },
          xAxis: {
            categories: months, // Use extracted months as categories
            labels: {
              formatter: function () {
                if (this.value.includes('maj')) return this.value.split(' ')[0];
                else return this.value.split('.')[0];
              },
            },
            title: {
              text: 'Måned',
            },
          },
          plotOptions: {
            column: {
              // maximum pixel width of each column — prevents columns from becoming tiny and spaced far apart
              maxPointWidth: 0,
              // reduce padding between columns in the single category
              groupPadding: 0.1,
              pointPadding: 0.1,
            },
            series: {
              // space the series slightly so they don't overlap; you can tweak this
              pointPlacement: 'between',
            },
          },
          yAxis: {
            min: 0,
            title: {
              text: `Total [${unit}]`,
            },
          },
          series: [
            {
              name: '24-36 måneder',
              data: previousYearData,
              tooltip: {
                headerFormat: '',
                valueDecimals: 0,
                valueSuffix: ` ${unit}`,
                pointFormatter: function () {
                  return `<span>${this.category.split(' ')[0]} ${
                    Number(this.category.split(' ')[1]) - 2
                  }</span><br/><span>${'Total forbrug'}</span>: <b>${formatNumber(
                    this.y,
                  )} ${unit}</b><br/>`;
                },
              },
            },
            {
              name: '12-24 måneder',
              data: lastYearData,
              tooltip: {
                headerFormat: '',
                valueDecimals: 0,
                valueSuffix: ` ${unit}`,
                pointFormatter: function () {
                  return `<span>${this.category.split(' ')[0]} ${
                    Number(this.category.split(' ')[1]) - 1
                  }</span><br/><span>${'Total forbrug'}</span>: <b>${formatNumber(
                    this.y,
                  )} ${unit}</b><br/>`;
                },
              },
            },
            {
              name: 'Seneste 12 måneder',
              data: thisYearData,
              tooltip: {
                headerFormat: '',
                valueDecimals: 0,
                valueSuffix: ` ${unit}`,
                pointFormatter: function () {
                  return `<span>${
                    this.category
                  }</span><br/><span>${'Total forbrug'}</span>: <b>${formatNumber(
                    this.y,
                  )} ${unit}</b><br/>`;
                },
              },
            },
          ],
        }
      : {
          // summary 3-bar mode — render the three summary bars inside a single category
          // so they stay close together (like flex space-around).
          chart: {
            type: 'column',
          },
          title: {
            text: ``,
          },
          // single category so series sit next to each other instead of being spread across the axis
          xAxis: {
            categories: ['Periode'],
            title: { text: '' },
            labels: { enabled: false },
          },
          yAxis: {
            min: 0,
            title: { text: `Total [${unit}]` },
          },
          // Keep the three summary columns visually close even on wide screens
          plotOptions: {
            column: {
              // maximum pixel width of each column — prevents columns from becoming tiny and spaced far apart
              maxPointWidth: 160,
              // reduce padding between columns in the single category
              groupPadding: 0.3,
              pointPadding: 0.1,
            },
            series: {
              // space the series slightly so they don't overlap; you can tweak this
              pointPlacement: 'between',
            },
          },
          // Build series so each has a single-point array (placed in the single category)
          series: summaryData.map((val, idx) => ({
            name: summaryCategories[idx],
            data: [val],
            tooltip: {
              headerFormat: '',
              valueDecimals: 0,
              valueSuffix: ` ${unit}`,
              pointFormatter: function () {
                return `<span>${summaryCategories[idx]}</span><br/><span>${'Total forbrug'}</span>: <b>${formatNumber(
                  this.y,
                )} ${unit}</b><br/>`;
              },
            },
            // show the name under the column via dataLabels if desired (disabled by default)
            dataLabels: {
              enabled: false,
            },
          })),
        };

  return (
    <div style={{ width: '100%', marginTop: 2 }}>
      {/* Toggle (MUI) */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 8,
          alignItems: 'center',
        }}
      >
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(e, value) => {
            if (value) setMode(value);
          }}
          size="small"
          aria-label="Visning"
        >
          <ToggleButton value="month" aria-label="Måned">
            Måned
          </ToggleButton>
          <ToggleButton sx={{ px: 2 }} value="year" aria-label="År">
            År
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
    </div>
  );
};

export default PortfolioAnalysisGroupedColumnChart;
